import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { switchMap, finalize, catchError } from 'rxjs/operators';

export interface ConversionReportItem {
  userId: string;
  userName: string;
  userEmail: string;
  usdtAmount: number;
  clpRate: number;
  clpTotal: number;
  transactionId: string;
  conversionDate: string;
}

export interface ConversionReportSummary {
  totalConversions: number;
  totalUsdtConverted: number;
  totalClpGenerated: number;
  averageRate: number;
}

export interface ConversionReportPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ConversionReportFilters {
  userId?: string;
  startDate?: string;
  endDate?: string;
}

export interface ConversionReportResponse {
  conversions: ConversionReportItem[];
  summary: ConversionReportSummary;
  pagination: ConversionReportPagination;
  filters: ConversionReportFilters;
}

export interface ConversionReportQueryParams {
  page?: number;
  limit?: number;
  userId?: string;
  startDate?: string;
  endDate?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConversionsReportService {
  private apiUrl = environment.apiNest;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  // Método para obtener los headers con el token
  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session || !session.accessToken || !session.accessToken.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }

    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      switch (error.status) {
        case 401:
          errorMessage =
            'Error de autenticación. Por favor, inicie sesión nuevamente.';
          break;
        case 403:
          errorMessage = 'No tiene permisos para realizar esta acción.';
          break;
        case 404:
          errorMessage = 'El servicio no está disponible en este momento.';
          break;
        case 500:
          errorMessage =
            'Error interno del servidor. Por favor, intente más tarde.';
          break;
        default:
          errorMessage = error.error?.message || errorMessage;
      }
    }

    return throwError(() => ({ message: errorMessage, originalError: error }));
  }

  /**
   * Obtiene el reporte de conversiones USDT a CLP con filtros y paginación
   */
  getConversionsReport(
    params: ConversionReportQueryParams = {},
  ): Observable<ConversionReportResponse> {
    this.loadingSubject.next(true);

    let httpParams = new HttpParams();
    if (params.page) httpParams = httpParams.set('page', params.page.toString());
    if (params.limit)
      httpParams = httpParams.set('limit', params.limit.toString());
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    if (params.startDate)
      httpParams = httpParams.set('startDate', params.startDate);
    if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<ConversionReportResponse>(
          `${this.apiUrl}binance-deposits/conversions`,
          { headers, params: httpParams },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }

  /**
   * Exporta el reporte de conversiones a Excel
   */
  exportToExcel(
    params: ConversionReportQueryParams = {},
  ): Observable<Blob> {
    this.loadingSubject.next(true);

    let httpParams = new HttpParams();
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    if (params.startDate)
      httpParams = httpParams.set('startDate', params.startDate);
    if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get(
          `${this.apiUrl}binance-deposits/conversions/export`,
          {
            headers,
            params: httpParams,
            responseType: 'blob',
          },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }

  /**
   * Descarga el archivo Excel
   */
  downloadExcelFile(blob: Blob, filename: string = 'conversiones-usdt-clp.xlsx') {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
